import React, { useEffect, useMemo, useRef, useState } from 'react'
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'


import {
  Botao,
  Breadcrumb,
  Cabecalho,
  Carregando,
  FormRef,
  IconeBack,
  IconeEditar,
  IconeLixeira,
  Link,
  Modal,
  ModalRef,
  MoedaInputUnform,
  Navegacao,
  SubTitulo,
  Tooltip
} from 'src/componentes'
import { Container as ContainerErroLayout } from 'src/componentes/erro-container-layout/styles'
import {
  focarPrimeiroCampoComErro,
  FuncoesMoeda
} from 'src/componentes/funcoes'
import {
  CalendarioUnform,
  CheckboxUnform,
  CpfInputUnform,
  SelectOpcao
} from 'src/componentes/unform'
import { FormUnform } from 'src/componentes/unform/form'
import { InputUnform } from 'src/componentes/unform/input'
import { SelectAsyncUnform } from 'src/componentes/unform/select/select-async'
import {
  RotasCondicaoEspecial,
  RotasCursos,
  RotasTurmas
} from 'src/rotas/academico'
import { Api } from 'src/servicos'
import {
  RequisitarCurso,
  RequisitarListaTurmasPorTexto,
  RequisitarTurmaPorId
} from 'src/servicos/api'
import {
  DestinoFormaPagamento,
  FormaPagamentoPorExtenso,
  ModoPaginaCadastro
} from 'src/tipos'
import { v4 as uuid } from 'uuid'

import { TooltipInformacao } from '../componentes/tooltip-informacao'
import {
  ModalOpcaoPagamento,
  ModalOpcaoRef
} from './componentes/modal-incluir-opcao'
import { schema } from './schema'
import {
  Aviso,
  CaixaAviso,
  CabecalhoSecao,
  CampoLeitura,
  LinkExterno,
  Container,
  ContainerAcoes,
  ContainerBotaoIcone,
  ContainerDados,
  ContainerDadosMedio,
  ContainerDadosPequeno,
  ContainerLink,
  ContainerMain,
  ContainerNavegacao,
  Conteudo,
  ResumoTurma,
  Secao,
  SemDados,
  Tabela
} from './styles'
import { FormularioCondicaoEspecial, OpcaoPagamentoLinha } from './tipos'

enum Navegacoes {
  Dados = 'dados-negociacao',
  Composicao = 'composicao',
  Opcoes = 'opcoes-pagamento'
}

interface PaginaCadastroProps
  extends RouteComponentProps<{ id?: string }> {
  modo: ModoPaginaCadastro
}

const CadastroCondicaoEspecial: React.FC<PaginaCadastroProps> = ({
  match,
  modo
}) => {
  const history = useHistory()
  const formRef = useRef<FormRef>(null)
  const modalCancelarRef = useRef<ModalRef>(null)
  const modalOpcaoRef = useRef<ModalOpcaoRef>(null)

  const ehEdicao = modo === ModoPaginaCadastro.Edicao
  /**
   * Duplicação é uma INCLUSÃO pré-preenchida: cria uma condição nova, e a origem
   * não é alterada. Por isso `ehEdicao` fica falso e a turma continua escolhível —
   * quem duplica pode querer a mesma negociação em outra turma.
   */
  const ehDuplicacao = modo === ModoPaginaCadastro.Duplicacao
  const precisaCarregar = ehEdicao || ehDuplicacao
  /** Id da condição lida: a editada, ou a que está sendo copiada. */
  const id = match.params.id

  const [pronto, definirPronto] = useState(!precisaCarregar)
  const [erro, definirErro] = useState<string>(null)
  const [carregando, definirCarregando] = useState(false)

  const [dadosIniciais, definirDadosIniciais] = useState<
    Partial<FormularioCondicaoEspecial>
  >({ isentarTaxaMatricula: false, temEntrada: false })

  const [opcoes, definirOpcoes] = useState<OpcaoPagamentoLinha[]>([])
  const [temEntrada, definirTemEntrada] = useState(false)
  const [isentarTaxa, definirIsentarTaxa] = useState(false)
  const [valorTaxa, definirValorTaxa] = useState<number>(null)
  /** Turma e curso já gravados — usados na edição, onde a turma não muda. */
  const [turmaSalva, definirTurmaSalva] = useState<{
    turmaId?: string
    turma?: string
    cursoId?: string
    curso?: string
  }>(null)

  // true: o curso da turma não tem contrato cadastrado. Aviso, não bloqueio — o
  // aceite funciona sem contrato, mas o aluno vê só o resumo das cláusulas.
  const [turmaSemContrato, definirTurmaSemContrato] = useState(false)

  const turmaIdSelecionada = turmaSalva?.turmaId

  useEffect(() => {
    definirTurmaSemContrato(false)
    if (!turmaIdSelecionada) return undefined

    let ativo = true

    Api.VerificarContratoTurma(turmaIdSelecionada)
      .then(possuiContrato => {
        if (ativo) definirTurmaSemContrato(!possuiContrato)
      })
      // O aviso é só informativo: se a consulta falhar, o formulário segue normal.
      .catch(() => undefined)

    return () => {
      ativo = false
    }
  }, [turmaIdSelecionada])

  const carregar = async () => {
    if (!precisaCarregar) return

    try {
      definirErro(null)
      const condicao = await Api.ObterCondicaoEspecialPorId(id)

      // Só a edição exige status editável. Duplicar vale para qualquer status —
      // inclusive cancelada, expirada ou concluída, que é justamente quando se
      // quer reaproveitar a composição.
      if (ehEdicao && !condicao.podeEditar) {
        toast(
          'Esta condição especial não pode mais ser alterada. Cancele e crie uma nova.',
          { type: 'info' }
        )
        history.replace(`${RotasCondicaoEspecial.Detalhe}/${id}`)
        return
      }

      // O que é da PESSOA ou do PRAZO da proposta original não vai para a cópia:
      //
      // - validade: a original costuma estar vencida, e a cópia já nasceria
      //   expirada; o consultor precisa definir uma nova;
      // - CPF, nome e e-mail do contato: o link é nominal, então copiar o CPF
      //   travaria a nova proposta para o aluno errado, e o convite iria para
      //   quem já recebeu o anterior;
      // - início da recorrência, quando já passou: cobraria parcelas no passado.
      //
      // Turma, valores e opções de pagamento — a negociação em si — são mantidos.
      const inicioRecorrenciaPassou =
        !!condicao.plano?.dataInicioRecorrencia &&
        new Date(condicao.plano.dataInicioRecorrencia) < new Date()

      definirDadosIniciais({
        turmaId: condicao.turmaId,
        validadeAte: ehDuplicacao ? undefined : condicao.validadeAte,
        nomeContato: ehDuplicacao ? undefined : condicao.nomeContato,
        emailConvite: ehDuplicacao ? undefined : condicao.emailConvite,
        cpfAluno: ehDuplicacao ? undefined : condicao.cpfAluno,
        valorTabela: condicao.plano?.valorTabela,
        taxaMatricula: condicao.plano?.taxaMatricula,
        isentarTaxaMatricula: condicao.plano?.taxaMatriculaInclusa ?? false,
        temEntrada: condicao.plano?.temEntrada ?? false,
        dataInicioRecorrencia:
          ehDuplicacao && inicioRecorrenciaPassou
            ? undefined
            : condicao.plano?.dataInicioRecorrencia
      })

      definirTemEntrada(condicao.plano?.temEntrada ?? false)
      // "Inclusa no valor" = a taxa não é cobrada à parte, ou seja, isenta.
      definirIsentarTaxa(condicao.plano?.taxaMatriculaInclusa ?? false)
      definirValorTaxa(condicao.plano?.taxaMatricula ?? null)

      definirOpcoes(
        (condicao.plano?.opcoes ?? []).map(o => ({
          id: uuid(),
          // Na duplicação as opções são novas: sem Id do servidor.
          opcaoId: ehDuplicacao ? undefined : o.id,
          destino: o.destino as DestinoFormaPagamento,
          formaPagamento: o.formaPagamento,
          valor: o.valor,
          quantidadeParcelas: o.quantidadeParcelas,
          valorParcela: o.valorParcela
        }))
      )

      // A API já devolve turma e curso; na edição eles são só exibidos.
      definirTurmaSalva({
        turmaId: condicao.turmaId,
        turma: condicao.turma,
        cursoId: condicao.cursoId,
        curso: condicao.curso
      })

      if (ehDuplicacao) {
        toast(
          `Duplicando ${condicao.numeroProtocolo}. Defina a validade e, se for o caso, o contato e o CPF do aluno.`,
          { type: 'info' }
        )
      }

      definirPronto(true)
    } catch {
      definirErro('Não foi possível carregar a condição especial.')
    }
  }

  useEffect(() => {
    carregar()
  }, [id]) //eslint-disable-line

  /**
   * Resolve o rótulo da turma pré-selecionada (duplicação).
   *
   * O select assíncrono chama `buscarPorId(valorInicial)` ao montar. Aqui isso
   * antes apontava para a busca por TEXTO, que procuraria turmas cujo nome é o
   * UUID e não acharia nenhuma — a turma da condição copiada nunca ficaria
   * selecionada. A turma já veio com a condição, então não precisa de nova ida à
   * API; qualquer outro valor cai na busca por texto, como antes.
   */
  const buscarTurmaPorId = async (valor: string): Promise<SelectOpcao[]> => {
    if (turmaSalva?.turmaId && turmaSalva.turmaId === valor) {
      return [{ id: turmaSalva.turmaId, texto: turmaSalva.turma ?? valor }]
    }

    return RequisitarListaTurmasPorTexto(valor)
  }

  const cursoDaTurma = useMemo(() => {
    if (ehEdicao) return turmaSalva?.curso ?? null
    return turmaSalva?.curso ?? null
  }, [ehEdicao, turmaSalva])

  /**
   * A busca de turma (RequisitarListaTurmasPorTexto) não devolve cursoId nem
   * preço — só id/nome/código. Ao selecionar, buscamos a turma completa (tem
   * cursoId) e depois o curso (tem precoBase/valorTaxaMatricula), preenchendo
   * os campos de composição como "copiado do curso" já promete na tela.
   */
  const aoSelecionarTurma = async (opcao: SelectOpcao | null) => {
    if (!opcao?.id) {
      definirTurmaSalva(null)
      return
    }

    try {
      const turma = await RequisitarTurmaPorId(opcao.id)
      definirTurmaSalva({
        turmaId: opcao.id,
        turma: opcao.texto,
        cursoId: turma?.cursoId,
        curso: turma?.nomeCurso
      })

      if (!turma?.cursoId) return

      const curso = await RequisitarCurso(turma.cursoId)

      // GET /api/Curso/{id}/outro devolve precoBase na raiz (Curso.PrecoBase
      // no backend), mas o tipo CursoPayload do frontend não declara esse
      // campo — daí o `as any`. Não é undefined em runtime, só falta na
      // tipagem existente.
      const precoBase = (curso as any)?.precoBase as number | undefined

      if (!formRef.current?.getFieldValue('valorTabela')) {
        formRef.current?.setFieldValue('valorTabela', precoBase)
      }
      if (!formRef.current?.getFieldValue('taxaMatricula')) {
        formRef.current?.setFieldValue(
          'taxaMatricula',
          curso?.valorTaxaMatricula
        )
        // setFieldValue não dispara o onChange do campo: sem isto, a linha da
        // taxa na tabela de opções ficava em "Valor do curso".
        definirValorTaxa(curso?.valorTaxaMatricula || null)
      }
    } catch {
      toast('Não foi possível carregar os valores do curso da turma.', {
        type: 'info'
      })
    }
  }

  const opcoesEntrada = useMemo(
    () => opcoes.filter(o => o.destino === DestinoFormaPagamento.Entrada),
    [opcoes]
  )

  const opcoesRestante = useMemo(
    () => opcoes.filter(o => o.destino === DestinoFormaPagamento.Restante),
    [opcoes]
  )

  // Desmarcar com opções de entrada cadastradas deixaria a composição inválida
  // (o backend recusa "não prevê entrada, mas há formas definidas para ela").
  const entradaBloqueada = opcoesEntrada.length > 0

  /**
   * Upsert de uma linha. A validação de valores e de duplicidade acontece no
   * modal, que já conhece as opções existentes.
   */
  const salvarOpcao = (opcaoSalva: OpcaoPagamentoLinha) =>
    definirOpcoes(atuais =>
      opcaoSalva.id
        ? atuais.map(o => (o.id === opcaoSalva.id ? opcaoSalva : o))
        : [...atuais, { ...opcaoSalva, id: uuid() }]
    )

  const excluirOpcao = (opcaoId: string) =>
    definirOpcoes(atuais => atuais.filter(o => o.id !== opcaoId))

  const acaoSucesso = async (dados: FormularioCondicaoEspecial) => {
    // Na edição a turma é só exibida (não há campo); na inclusão e na duplicação
    // ela vem do select.
    const turmaId = ehEdicao ? dadosIniciais.turmaId : dados.turmaId

    if (!turmaId) {
      toast('Informe a turma da condição especial.', { type: 'info' })
      return
    }

    // Na inclusão e na duplicação não se cria condição de curso sem contrato (o backend
    // também recusa); na edição é só o aviso, para não prender condições já existentes.
    if (!ehEdicao && turmaSemContrato) {
      toast(
        'O curso desta turma não tem contrato disponível. Cadastre o contrato na edição do curso.',
        { type: 'info' }
      )
      return
    }

    if (!opcoes.length) {
      toast('Informe ao menos uma forma de pagamento.', { type: 'info' })
      return
    }

    if (temEntrada && !opcoesEntrada.length) {
      toast(
        'Esta condição especial prevê entrada, mas nenhuma forma de pagamento foi definida para ela.',
        { type: 'info' }
      )
      return
    }

    if (!temEntrada && opcoesEntrada.length) {
      toast(
        'Esta condição especial não prevê entrada, mas há formas de pagamento definidas para ela.',
        { type: 'info' }
      )
      return
    }

    if (!opcoesRestante.length) {
      toast(
        'Informe ao menos uma forma de pagamento para o restante do curso.',
        { type: 'info' }
      )
      return
    }

    try {
      definirCarregando(true)

      const payload = {
        turmaId,
        validadeAte: dados.validadeAte,
        cpfAluno: dados.cpfAluno || undefined,
        emailConvite: dados.emailConvite || undefined,
        nomeContato: dados.nomeContato || undefined,
        valorTabela: dados.valorTabela || undefined,
        taxaMatricula: dados.taxaMatricula || undefined,
        taxaMatriculaInclusa: isentarTaxa,
        temEntrada,
        dataInicioRecorrencia: dados.dataInicioRecorrencia || undefined,
        opcoes: opcoes.map(o => ({
          id: o.opcaoId,
          destino: o.destino,
          formaPagamento: o.formaPagamento,
          valor: o.valor,
          quantidadeParcelas: o.quantidadeParcelas,
          valorParcela: o.valorParcela
        }))
      }

      if (ehEdicao) {
        await Api.EditarCondicaoEspecial(id, payload)
        toast('Condição especial editada com sucesso.', { type: 'success' })
        history.push(`${RotasCondicaoEspecial.Detalhe}/${id}`)
      } else {
        const criada = await Api.CriarCondicaoEspecial(payload)
        toast(
          `Condição especial ${criada.numeroProtocolo} criada com sucesso.`,
          { type: 'success' }
        )
        history.push(`${RotasCondicaoEspecial.Detalhe}/${criada.id}`)
      }
    } catch {
      // Mensagens de validação do backend já viram toast no interceptor.
    } finally {
      definirCarregando(false)
    }
  }

  if (erro) {
    return (
      <ContainerErroLayout>
        <p>{erro}</p>
        <Botao
          type="button"
          texto="Tentar novamente"
          tema="Secundario"
          onClick={() => {
            definirErro(null)
            carregar()
          }}
        />
      </ContainerErroLayout>
    )
  }

  if (!pronto) {
    return <Carregando texto="Carregando condição especial ..." />
  }

  return (
    <>
      <ContainerLink>
        <Link
          texto="Voltar"
          icone={IconeBack}
          acaoVoltar={() => history.goBack()}
        />
      </ContainerLink>
      <Cabecalho>
        <Breadcrumb
          titulo={
            ehEdicao
              ? 'Editar condição especial'
              : ehDuplicacao
              ? 'Duplicar condição especial'
              : 'Nova condição especial'
          }
          atalhos={[
            { texto: 'Financeiro' },
            {
              texto: 'Condição especial',
              acao: () => history.push(RotasCondicaoEspecial.Lista)
            },
            {
              texto: ehEdicao ? 'Edição' : ehDuplicacao ? 'Duplicação' : 'Cadastro'
            }
          ]}
        />
      </Cabecalho>
      <Container>
        <ContainerMain>
          <FormUnform
            ref={formRef}
            schema={schema}
            dadosIniciais={dadosIniciais}
            acaoSucesso={acaoSucesso}
            acaoFalha={focarPrimeiroCampoComErro}
          >
            <Conteudo>
              <Secao id={Navegacoes.Dados}>
                <SubTitulo texto="Dados da negociação" />
                <ContainerDados>
                  <ContainerDadosMedio>
                    {ehEdicao ? (
                      // A turma define curso, preço e onde o aluno se matricula
                      // — trocá-la depois invalidaria a composição inteira.
                      <CampoLeitura>
                        <span className="rotulo">
                          Turma
                          <TooltipInformacao
                            id="tooltip-turma-nao-editavel"
                            mensagem="A turma não pode ser alterada. Cancele e crie uma nova condição especial."
                          />
                        </span>
                        <span className="valor">
                          {turmaSalva?.turmaId ? (
                            <LinkExterno
                              href={`${RotasTurmas.Edicao}/${turmaSalva.turmaId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {turmaSalva.turma ?? 'Abrir turma'}
                            </LinkExterno>
                          ) : (
                            turmaSalva?.turma ?? '—'
                          )}
                        </span>
                      </CampoLeitura>
                    ) : (
                      <SelectAsyncUnform
                        id="slct_turma"
                        name="turmaId"
                        label="Turma"
                        obrigatorio
                        buscarPorTexto={RequisitarListaTurmasPorTexto}
                        buscarPorId={buscarTurmaPorId}
                        valorAlterado={aoSelecionarTurma}
                      />
                    )}
                  </ContainerDadosMedio>
                  <ContainerDadosPequeno>
                    <CalendarioUnform
                      className="sm"
                      id="ipt_validade_ate"
                      name="validadeAte"
                      label="Validade do link"
                      obrigatorio
                    />
                  </ContainerDadosPequeno>
                </ContainerDados>

                {turmaSemContrato && (
                  <CaixaAviso role="alert">
                    O contrato do curso desta turma não está disponível (não está
                    cadastrado ou o arquivo não foi encontrado).{' '}
                    {ehEdicao
                      ? 'O aluno verá apenas o resumo das cláusulas, sem o PDF do contrato. '
                      : 'Não é possível criar a condição especial sem o contrato. '}
                    {turmaSalva?.cursoId && (
                      <LinkExterno
                        href={`${RotasCursos.Edicao}/${turmaSalva.cursoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Cadastrar o contrato na edição do curso
                      </LinkExterno>
                    )}
                  </CaixaAviso>
                )}

                {cursoDaTurma && (
                  <ResumoTurma>
                    <div>
                      <span className="rotulo">Curso da turma</span>
                      <span className="valor">
                        {turmaSalva?.cursoId ? (
                          <LinkExterno
                            href={`${RotasCursos.Edicao}/${turmaSalva.cursoId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {cursoDaTurma}
                          </LinkExterno>
                        ) : (
                          cursoDaTurma
                        )}
                      </span>
                    </div>
                  </ResumoTurma>
                )}

                <ContainerDados>
                  <ContainerDadosMedio>
                    <InputUnform
                      type="text"
                      id="ipt_nome_contato"
                      name="nomeContato"
                      label="Nome do contato"
                      maxLength={200}
                    />
                  </ContainerDadosMedio>
                  <ContainerDadosMedio>
                    <InputUnform
                      type="text"
                      id="ipt_email_convite"
                      name="emailConvite"
                      label="E-mail de convite"
                      maxLength={256}
                    />
                  </ContainerDadosMedio>
                </ContainerDados>
                <ContainerDados>
                  <ContainerDadosPequeno>
                    <CpfInputUnform
                      type="text"
                      id="ipt_cpf_aluno"
                      name="cpfAluno"
                      semMargemAbaixo
                      label={
                        <>
                          CPF do aluno (opcional)
                          <TooltipInformacao
                            id="tooltip-cpf-aluno"
                            mensagem="Se preenchido, o aceite com outro CPF é bloqueado."
                          />
                        </>
                      }
                    />
                  </ContainerDadosPequeno>
                </ContainerDados>
              </Secao>

              <Secao id={Navegacoes.Composicao}>
                <SubTitulo texto="Composição" />
                <ContainerDados>
                  <ContainerDadosPequeno>
                    <MoedaInputUnform
                      id="ipt_valor_tabela"
                      name="valorTabela"
                      label={
                        <>
                          Valor de tabela
                          <TooltipInformacao
                            id="tooltip-valor-tabela"
                            mensagem="Valor de tabela e taxa de matrícula são copiados do curso."
                          />
                        </>
                      }
                    />
                  </ContainerDadosPequeno>
                  <ContainerDadosPequeno>
                    <MoedaInputUnform
                      id="ipt_taxa_matricula"
                      name="taxaMatricula"
                      label={
                        <>
                          Taxa de matrícula
                          <TooltipInformacao
                            id="tooltip-taxa-matricula"
                            mensagem="Valor de tabela e taxa de matrícula são copiados do curso."
                          />
                        </>
                      }
                      onChange={e =>
                        definirValorTaxa(
                          Number(
                            String(e.target.value)
                              .replace(/[R$\s.]/g, '')
                              .replace(',', '.')
                          ) || null
                        )
                      }
                    />
                  </ContainerDadosPequeno>
                  <ContainerDadosPequeno>
                    <CalendarioUnform
                      className="sm"
                      id="ipt_data_inicio_recorrencia"
                      name="dataInicioRecorrencia"
                      label={
                        <>
                          Início da recorrência
                          <TooltipInformacao
                            id="tooltip-inicio-recorrencia"
                            mensagem="Início da recorrência em branco cobra a primeira parcela junto da matrícula."
                          />
                        </>
                      }
                    />
                  </ContainerDadosPequeno>
                </ContainerDados>
                <ContainerDados>
                  <ContainerDadosMedio>
                    <CheckboxUnform
                      id="chk_isentar_taxa"
                      name="isentarTaxaMatricula"
                      texto="Isentar taxa de matrícula"
                      checked={isentarTaxa}
                      onChange={e => definirIsentarTaxa(e.target.checked)}
                    />
                  </ContainerDadosMedio>
                  <ContainerDadosMedio>
                    <CheckboxUnform
                      id="chk_tem_entrada"
                      name="temEntrada"
                      texto="Tem entrada"
                      checked={temEntrada}
                      disabled={entradaBloqueada}
                      onChange={e => definirTemEntrada(e.target.checked)}
                    />
                    {entradaBloqueada && (
                      <Aviso>
                        Exclua as opções de entrada antes de desmarcar.
                      </Aviso>
                    )}
                  </ContainerDadosMedio>
                </ContainerDados>
              </Secao>

              <Secao id={Navegacoes.Opcoes}>
                <CabecalhoSecao>
                  <SubTitulo texto="Opções de pagamento" />
                  <Botao
                    texto="Adicionar opção"
                    tema="Secundario"
                    type="button"
                    onClick={() => modalOpcaoRef.current?.abrir()}
                  />
                </CabecalhoSecao>

                <p style={{ fontSize: 12, marginTop: 4 }}>
                  Cada forma de pagamento pode ter um preço próprio. O aluno
                  escolhe uma das opções no link.
                </p>

                {(opcoes.length > 0 || !isentarTaxa) && (
                  <Tabela>
                    <thead>
                      <tr>
                        <th>Destino</th>
                        <th>Forma de pagamento</th>
                        <th>Valor total</th>
                        <th>Parcelas</th>
                        <th>Valor da parcela</th>
                        <th />
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {/*
                        A taxa de matrícula não é uma opção de pagamento no
                        backend (DestinoFormaPagamento só tem Entrada e
                        Restante) — é campo próprio do plano. Aqui ela aparece
                        como linha só para o consultor enxergar a composição
                        completa, e some quando a isenção é marcada.
                      */}
                      {!isentarTaxa && (
                        <tr className="taxa">
                          <td align="center">Taxa de matrícula</td>
                          <td align="center">—</td>
                          <td align="center">
                            {valorTaxa
                              ? FuncoesMoeda.formatarMoeda(valorTaxa)
                              : 'Valor do curso'}
                          </td>
                          <td align="center">1</td>
                          <td align="center">
                            {valorTaxa
                              ? FuncoesMoeda.formatarMoeda(valorTaxa)
                              : 'Valor do curso'}
                          </td>
                          <td align="center" colSpan={2} />
                        </tr>
                      )}
                      {opcoes.map((opcao, indice) => (
                        <tr
                          key={opcao.id}
                          className={indice % 2 === 1 ? 'par' : ''}
                        >
                          <td align="center">{opcao.destino}</td>
                          <td align="center">
                            {FormaPagamentoPorExtenso[
                              opcao.formaPagamento as keyof typeof FormaPagamentoPorExtenso
                            ] ?? opcao.formaPagamento}
                          </td>
                          <td align="center">
                            {FuncoesMoeda.formatarMoeda(opcao.valor)}
                          </td>
                          <td align="center">{opcao.quantidadeParcelas}</td>
                          <td align="center">
                            {FuncoesMoeda.formatarMoeda(opcao.valorParcela)}
                          </td>
                          <td align="center">
                            <ContainerBotaoIcone>
                              <button
                                type="button"
                                data-tip="Editar opção"
                                data-for="tooltip-editar-opcao"
                                onClick={() =>
                                  modalOpcaoRef.current?.abrir(opcao)
                                }
                              >
                                {IconeEditar}
                              </button>
                              <Tooltip
                                id="tooltip-editar-opcao"
                                place="bottom"
                                offset={{ top: 10 }}
                              />
                            </ContainerBotaoIcone>
                          </td>
                          <td align="center">
                            <ContainerBotaoIcone>
                              <button
                                type="button"
                                data-tip="Excluir opção"
                                data-for="tooltip-excluir-opcao"
                                onClick={() => excluirOpcao(opcao.id)}
                              >
                                {IconeLixeira}
                              </button>
                              <Tooltip
                                id="tooltip-excluir-opcao"
                                place="bottom"
                                offset={{ top: 10 }}
                              />
                            </ContainerBotaoIcone>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Tabela>
                )}

                {!opcoes.length && (
                  <SemDados>Nenhuma opção de pagamento adicionada.</SemDados>
                )}
              </Secao>

              <ContainerAcoes>
                <Botao
                  texto="Cancelar"
                  tema="Secundario"
                  type="button"
                  onClick={() => modalCancelarRef.current?.abrir()}
                />
                <Botao
                  type="submit"
                  texto="Salvar"
                  carregando={carregando}
                />
              </ContainerAcoes>
            </Conteudo>
          </FormUnform>
        </ContainerMain>
        <ContainerNavegacao>
          <Navegacao
            itens={[
              { nome: 'Dados da negociação', link: Navegacoes.Dados },
              { nome: 'Composição', link: Navegacoes.Composicao },
              { nome: 'Opções de pagamento', link: Navegacoes.Opcoes }
            ]}
          />
        </ContainerNavegacao>
      </Container>

      <ModalOpcaoPagamento
        ref={modalOpcaoRef}
        backdrop
        temEntrada={temEntrada}
        opcoesExistentes={opcoes}
        acaoPrimaria={salvarOpcao}
      />

      <Modal
        ref={modalCancelarRef}
        backdrop
        id="modal-confirmacao-cancelamento"
        icone={<></>}
        titulo="Deseja cancelar?"
        acaoPrimaria={{
          titulo: 'Sim',
          tipo: 'button',
          acao: () => history.push(RotasCondicaoEspecial.Lista)
        }}
        acaoSecundario={{
          titulo: 'Não',
          tipo: 'button',
          acao: () => modalCancelarRef.current?.fechar()
        }}
      >
        <p>As alterações não salvas serão perdidas.</p>
      </Modal>
    </>
  )
}

export default withRouter(CadastroCondicaoEspecial)
